import React, { useRef, useState, useEffect } from "react";
import { X, Printer } from "lucide-react";
import { MONTHS, PAYSLIP_STATUS_COLORS, PAYSLIP_STATUS_LABELS } from "../../constants/hrConstants";
import { useAuthStore } from "@/store/authStore";
import { getCompanySettings } from "../../../verve-settings/services/settingsService";
import { API_BASE_URL } from "@/services/api";

const formatLogoUrl = (path) => {
  if (!path || path === "undefined" || path === "null") return "";
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  let cleanPath = path.replace(/\\/g, "/");
  cleanPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  return `${API_BASE_URL}${cleanPath}`;
};

// Convert number to Indian English words representation
function numberToWords(num) {
  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convert = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " and " + convert(n % 100) : "");
    if (n < 100000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
    if (n < 10000000) return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
    return convert(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + convert(n % 10000000) : "");
  };

  if (num === 0) return "Zero";
  return convert(num);
}

export function PayslipPreview({ payslip, onClose }) {
  const [fetchedSettings, setFetchedSettings] = useState(null);
  const printRef = useRef();
  const { companySettings } = useAuthStore();

  useEffect(() => {
    getCompanySettings()
      .then((res) => {
        const payload = res?.data?.data || res?.data || {};
        setFetchedSettings(payload);
      })
      .catch((err) => {
        console.warn("Failed to fetch company settings for payslip preview", err);
      });
  }, []);

  if (!payslip) return null;

  const monthName = MONTHS[Number(payslip.month) - 1] || payslip.month;
  const earnings = payslip.components?.filter((c) => c.type === "earning") || [];
  const deductions = payslip.components?.filter((c) => c.type === "deduction") || [];

  const rawCompanyName = payslip.companyDetails?.name || fetchedSettings?.companyName || companySettings?.name;
  const companyName = (!rawCompanyName || rawCompanyName.toLowerCase().includes("venus"))
    ? "Verve Kitchens"
    : rawCompanyName;

  const companyAddress = payslip.companyDetails?.address || fetchedSettings?.companyAddress || companySettings?.address || "";
  const companyCity = payslip.companyDetails?.city || fetchedSettings?.companyState || companySettings?.city || "";
  const companyCountry = payslip.companyDetails?.country || "India";

  const rawLogoPath = payslip.companyDetails?.logoUrl || fetchedSettings?.logoExpandedPath || companySettings?.logoUrl || "";
  const companyLogoUrl = formatLogoUrl(rawLogoPath);

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "_blank", "width=800,height=900");
    win.document.write(`
      <html>
        <head>
          <title>Payslip - ${payslip.employeeName} - ${monthName} ${payslip.year}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Inter', 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #fff; padding: 32px 36px; font-size: 12px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

            .payslip-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 16px; border-bottom: 2px solid #e2e8f0; margin-bottom: 20px; }
            .company-info { display: flex; align-items: center; gap: 14px; }
            .company-logo { width: 52px; height: 52px; border-radius: 10px; background: #f1f5f9; border: 1px solid #e2e8f0; padding: 4px; object-fit: contain; flex-shrink: 0; }
            .company-name { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -0.02em; }
            .company-addr { font-size: 10.5px; color: #64748b; margin-top: 2px; font-weight: 400; }

            .period-badge { text-align: right; }
            .period-label { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.08em; }
            .period-value { font-size: 17px; font-weight: 800; color: #1e40af; margin-top: 3px; }

            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
            .info-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; }
            .info-card-title { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 10px; }
            .info-row { display: flex; justify-content: space-between; align-items: baseline; padding: 4px 0; border-bottom: 1px solid #f1f5f9; }
            .info-row:last-child { border-bottom: none; }
            .info-label { font-size: 11px; color: #64748b; font-weight: 500; }
            .info-value { font-size: 11px; font-weight: 600; color: #1e293b; text-align: right; }

            .net-highlight { background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border: 1px solid #a7f3d0; border-radius: 12px; padding: 18px 20px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; }
            .net-amount { font-size: 26px; font-weight: 900; color: #047857; letter-spacing: -0.02em; }
            .net-label { font-size: 10px; font-weight: 700; color: #059669; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 2px; }
            .net-days { display: flex; gap: 20px; font-size: 11px; font-weight: 600; color: #047857; }

            .tables-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 18px; }
            .table-section-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px; padding-left: 2px; }
            .earnings-title { color: #047857; }
            .deductions-title { color: #b91c1c; }

            .comp-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
            .comp-table th { padding: 8px 14px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e2e8f0; }
            .comp-table .earn-th { background: #ecfdf5; color: #047857; }
            .comp-table .ded-th { background: #fef2f2; color: #b91c1c; }
            .comp-table td { padding: 7px 14px; font-size: 11px; color: #334155; border-bottom: 1px solid #f1f5f9; }
            .comp-table tr:last-child td { border-bottom: none; }
            .comp-table .total-row { background: #f8fafc; font-weight: 700; }
            .comp-table .total-row td { border-top: 1px solid #e2e8f0; padding: 9px 14px; color: #0f172a; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }

            .total-bar { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 18px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
            .total-bar-label { font-size: 12px; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 0.03em; }
            .total-bar-value { font-size: 15px; font-weight: 800; color: #047857; }

            .words-section { font-size: 10.5px; color: #64748b; font-style: italic; padding: 12px 0; border-top: 1px dashed #cbd5e1; margin-top: 6px; }
            .words-label { font-weight: 700; color: #475569; font-style: normal; text-transform: uppercase; font-size: 9px; letter-spacing: 0.06em; }

            .print-footer { margin-top: 32px; padding-top: 14px; border-top: 1px dashed #cbd5e1; text-align: center; font-size: 9.5px; color: #94a3b8; font-weight: 500; }

            @media print {
              body { padding: 20px; }
              .payslip-header { break-inside: avoid; }
              .info-grid { break-inside: avoid; }
              .tables-grid { break-inside: avoid; }
            }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  // Helper to format role strings nicely
  const formatRole = (role) => {
    if (!role) return "—";
    return role.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop blur */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal Container */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 border border-slate-100">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${PAYSLIP_STATUS_COLORS[payslip.status]}`}>
              {PAYSLIP_STATUS_LABELS[payslip.status]}
            </span>
            <h2 className="text-[15px] font-bold text-slate-800">Payslip — {monthName} {payslip.year}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="btn-secondary flex items-center gap-1.5 text-[12px]">
              <Printer className="w-3.5 h-3.5" /> Print / Download
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scrollable Document Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          <div ref={printRef} className="bg-white border border-slate-200 rounded-2xl p-8 max-w-3xl mx-auto shadow-sm">
            
            {/* ═══════ Header ═══════ */}
            <div className="payslip-header flex justify-between items-start pb-4 border-b-2 border-slate-200 mb-5">
              <div className="company-info flex items-center gap-3.5">
                {companyLogoUrl ? (
                  <img src={companyLogoUrl} alt="Logo" className="company-logo w-[52px] h-[52px] rounded-[10px] border border-slate-200 p-1 object-contain bg-slate-50 flex-shrink-0" />
                ) : (
                  <div className="company-logo w-[52px] h-[52px] rounded-[10px] bg-[#1e40af]/10 text-[#1e40af] flex items-center justify-center font-black text-[22px] flex-shrink-0">
                    {companyName.charAt(0)}
                  </div>
                )}
                <div>
                  <h1 className="company-name text-[18px] font-extrabold text-slate-900 leading-tight tracking-tight">{companyName}</h1>
                  <p className="company-addr text-[10.5px] text-slate-500 mt-1">
                    {[companyAddress, companyCity, companyCountry].filter(Boolean).join(", ")}
                  </p>
                </div>
              </div>
              <div className="period-badge text-right">
                <p className="period-label text-[9px] font-bold text-slate-400 uppercase tracking-widest">Payslip For</p>
                <p className="period-value text-[17px] font-extrabold text-[#1e40af] mt-1">{monthName} {payslip.year}</p>
              </div>
            </div>

            {/* ═══════ Two Info Cards Side by Side ═══════ */}
            <div className="info-grid grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              
              {/* Employee Details Card */}
              <div className="info-card bg-slate-50 border border-slate-100 rounded-xl p-4">
                <p className="info-card-title text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Employee Details</p>
                <div className="space-y-1.5">
                  <div className="info-row flex justify-between items-baseline py-1 border-b border-slate-100">
                    <span className="info-label text-[11px] text-slate-500 font-medium">Employee Name</span>
                    <span className="info-value text-[11px] font-bold text-slate-800 capitalize">{payslip.employeeName}</span>
                  </div>
                  <div className="info-row flex justify-between items-baseline py-1 border-b border-slate-100">
                    <span className="info-label text-[11px] text-slate-500 font-medium">Employee ID</span>
                    <span className="info-value text-[11px] font-semibold font-mono text-slate-700">{payslip.employeeId}</span>
                  </div>
                  <div className="info-row flex justify-between items-baseline py-1 border-b border-slate-100">
                    <span className="info-label text-[11px] text-slate-500 font-medium">Department</span>
                    <span className="info-value text-[11px] font-semibold text-slate-700 capitalize">{payslip.department || "—"}</span>
                  </div>
                  <div className="info-row flex justify-between items-baseline py-1 border-b border-slate-100">
                    <span className="info-label text-[11px] text-slate-500 font-medium">Designation</span>
                    <span className="info-value text-[11px] font-semibold text-slate-700 capitalize">{payslip.designation || "—"}</span>
                  </div>
                  <div className="info-row flex justify-between items-baseline py-1">
                    <span className="info-label text-[11px] text-slate-500 font-medium">Role</span>
                    <span className="info-value text-[11px] font-semibold text-slate-700">{formatRole(payslip.role)}</span>
                  </div>
                </div>
              </div>

              {/* Payroll Details Card */}
              <div className="info-card bg-slate-50 border border-slate-100 rounded-xl p-4">
                <p className="info-card-title text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Payroll Details</p>
                <div className="space-y-1.5">
                  <div className="info-row flex justify-between items-baseline py-1 border-b border-slate-100">
                    <span className="info-label text-[11px] text-slate-500 font-medium">Pay Period</span>
                    <span className="info-value text-[11px] font-semibold text-slate-700">{monthName} {payslip.year}</span>
                  </div>
                  <div className="info-row flex justify-between items-baseline py-1 border-b border-slate-100">
                    <span className="info-label text-[11px] text-slate-500 font-medium">Pay Date</span>
                    <span className="info-value text-[11px] font-semibold text-slate-700">{new Date(payslip.generatedOn).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                  <div className="info-row flex justify-between items-baseline py-1 border-b border-slate-100">
                    <span className="info-label text-[11px] text-slate-500 font-medium">PF Number</span>
                    <span className="info-value text-[11px] font-semibold font-mono text-slate-700">{payslip.pfNumber || "—"}</span>
                  </div>
                  <div className="info-row flex justify-between items-baseline py-1">
                    <span className="info-label text-[11px] text-slate-500 font-medium">Working / Present / LOP</span>
                    <span className="info-value text-[11px] font-semibold text-slate-700">{payslip.workingDays} / {payslip.presentDays} / {payslip.leaveDays}</span>
                  </div>
                </div>
              </div>

            </div>

            {/* ═══════ Net Pay Highlight ═══════ */}
            <div className="net-highlight bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4 mb-5 flex justify-between items-center">
              <div>
                <span className="net-amount text-[24px] font-black text-emerald-700">₹{Number(payslip.netPay).toLocaleString("en-IN")}</span>
                <p className="net-label text-[10px] font-bold text-emerald-600 uppercase tracking-wider mt-0.5">Total Net Pay</p>
              </div>
              <div className="net-days flex gap-5 text-[11px] font-semibold text-emerald-700">
                <span>Paid Days: {payslip.presentDays}</span>
                <span>LOP Days: {payslip.leaveDays}</span>
              </div>
            </div>

            {/* ═══════ Earnings & Deductions Tables ═══════ */}
            <div className="tables-grid grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              
              {/* Earnings Table */}
              <div>
                <p className="table-section-title text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-2 earnings-title">Earnings</p>
                <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                  <table className="comp-table w-full text-[12px]">
                    <thead>
                      <tr>
                        <th className="earn-th text-left px-3.5 py-2.5 text-[10px] font-bold bg-emerald-50 text-emerald-700 border-b border-slate-200">Component</th>
                        <th className="earn-th text-right px-3.5 py-2.5 text-[10px] font-bold bg-emerald-50 text-emerald-700 border-b border-slate-200">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {earnings.map((e, idx) => (
                        <tr key={idx}>
                          <td className="px-3.5 py-2 text-slate-600 border-b border-slate-50">{e.name}</td>
                          <td className="px-3.5 py-2 text-slate-800 text-right border-b border-slate-50 font-medium">₹{Number(e.amount).toLocaleString("en-IN")}</td>
                        </tr>
                      ))}
                      <tr className="total-row bg-slate-50 font-bold border-t border-slate-200">
                        <td className="px-3.5 py-2.5 text-slate-800 font-bold">Gross Earnings</td>
                        <td className="px-3.5 py-2.5 text-emerald-700 text-right font-bold">₹{Number(payslip.grossEarnings).toLocaleString("en-IN")}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Deductions Table */}
              <div>
                <p className="table-section-title text-[10px] font-bold text-red-600 uppercase tracking-wider mb-2 deductions-title">Deductions</p>
                <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                  <table className="comp-table w-full text-[12px]">
                    <thead>
                      <tr>
                        <th className="ded-th text-left px-3.5 py-2.5 text-[10px] font-bold bg-red-50 text-red-700 border-b border-slate-200">Component</th>
                        <th className="ded-th text-right px-3.5 py-2.5 text-[10px] font-bold bg-red-50 text-red-700 border-b border-slate-200">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {deductions.map((d, idx) => (
                        <tr key={idx}>
                          <td className="px-3.5 py-2 text-slate-600 border-b border-slate-50">{d.name}</td>
                          <td className="px-3.5 py-2 text-slate-800 text-right border-b border-slate-50 font-medium">₹{Number(d.amount).toLocaleString("en-IN")}</td>
                        </tr>
                      ))}
                      <tr className="total-row bg-slate-50 font-bold border-t border-slate-200">
                        <td className="px-3.5 py-2.5 text-slate-800 font-bold">Total Deductions</td>
                        <td className="px-3.5 py-2.5 text-red-600 text-right font-bold">₹{Number(payslip.totalDeductions).toLocaleString("en-IN")}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            {/* ═══════ Total Net Payable Bar ═══════ */}
            <div className="total-bar border border-slate-200 rounded-xl p-3.5 bg-slate-50 flex justify-between items-center shadow-sm">
              <span className="total-bar-label text-[12px] font-extrabold text-slate-800 uppercase tracking-wide">Total Net Payable</span>
              <span className="total-bar-value text-[15px] font-extrabold text-emerald-700">₹{Number(payslip.netPay).toLocaleString("en-IN")}</span>
            </div>

            {/* ═══════ Amount in Words ═══════ */}
            <div className="words-section mt-4 pt-3.5 border-t border-dashed border-slate-200 text-[10.5px] text-slate-500 italic leading-relaxed">
              <span className="words-label font-bold text-slate-600 not-italic uppercase tracking-widest text-[9px]">Amount In Words:</span>
              <br />
              Indian Rupees {numberToWords(Math.max(0, payslip.netPay))} Only
            </div>

            {/* ═══════ Footer ═══════ */}
            <div className="print-footer mt-8 pt-3 border-t border-dashed border-slate-200 text-center text-[9.5px] text-slate-400 font-medium">
              — This is a computer-generated document and does not require a physical signature. —
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}
